import { Box, Grid, useTheme } from '@simplywallst/ui-core';

import PencilOutline from '@simplywallst/ui-core/icons/PencilOutline';
import PriceTargetOutline from '@simplywallst/ui-core/icons/PriceTargetOutline';
import UserOutline from '@simplywallst/ui-core/icons/UserOutline';
import WarrenSolid from '@simplywallst/ui-core/icons/WarrenSolid';

import type { CurrencyISO } from '@/constants/currencies';

import FairValue from './FairValue';

import { isNarrativeFairValueType } from '../../types';
import { NarrativeAuthorAvatar } from '@/components/NarrativeAuthor';
import { useTracking } from 'react-tracking';

export interface Props {
  currencyISO: CurrencyISO;
  narrativeFairValue: {
    fairValue: number | null;
    type: string | null;
    valuationId: string | null;
    author: {
      id: string;
      displayName: string | null;
      name: string;
      imageUrl: string | null;
    } | null;
  };
  npvPerShare: number | null;
  onClick: () => void;
}

const NarrativeFairValue = ({
  currencyISO,
  narrativeFairValue,
  npvPerShare,
  onClick,
}: Props) => {
  const theme = useTheme();
  const { trackEvent } = useTracking({
    type: 'track',
  });
  const { fairValue, type, valuationId, author } = narrativeFairValue;

  const fairValueType = isNarrativeFairValueType(type) ? type : undefined;
  return (
    <Grid>
      <Box
        backgroundColor="spaceGray"
        borderRadius="x0_5"
        paddingY="x0_5"
        paddingX="x1"
        cursor="pointer"
        onClick={() => {
          trackEvent({
            subject: 'narrative-fair-value',
            action: 'click',
          });
          onClick();
        }}
        data-cy-id="narrative-fair-value"
      >
        <Grid
          columnGap="8px"
          templateColumns="min-content 1fr min-content"
          alignItems="center"
        >
          {(() => {
            if (valuationId === null) {
              return (
                <FairValue
                  value={npvPerShare}
                  icon={<WarrenSolid height={20} width={20} />}
                  currencyISO={currencyISO}
                  type={fairValueType}
                />
              );
            }
            switch (type) {
              case 'dcf':
                return (
                  <FairValue
                    value={fairValue}
                    icon={<WarrenSolid height={20} width={20} />}
                    currencyISO={currencyISO}
                    type={type}
                  />
                );
              case 'legacy':
                return (
                  <FairValue
                    value={fairValue}
                    icon={
                      <UserOutline
                        fill={theme.color.white}
                        height={20}
                        width={20}
                      />
                    }
                    currencyISO={currencyISO}
                    type={type}
                  />
                );
              case 'user_defined':
                return (
                  <FairValue
                    value={fairValue}
                    icon={
                      author ? (
                        <NarrativeAuthorAvatar size="sm" author={author} />
                      ) : (
                        <UserOutline
                          fill={theme.color.white}
                          height={20}
                          width={20}
                        />
                      )
                    }
                    currencyISO={currencyISO}
                    type={type}
                  />
                );

              case 'analysts':
                return (
                  <FairValue
                    value={fairValue}
                    icon={<PriceTargetOutline fill={theme.color.brand01} />}
                    currencyISO={currencyISO}
                    type={type}
                  />
                );
              default:
                return (
                  <FairValue
                    value={npvPerShare}
                    icon={<WarrenSolid height={20} width={20} />}
                    currencyISO={currencyISO}
                    type={fairValueType}
                  />
                );
            }
          })()}

          <PencilOutline fill={theme.color.brand01} />
        </Grid>
      </Box>
    </Grid>
  );
};

export default NarrativeFairValue;
